import { Persona, TableColumnDefinition } from '@fluentui/react-components'
import {
   DemandAnalysis,
   DemandAnalysisQuestion,
   AnswerType,
   DemandAnalysisProcessOwner,
   DemandLevel,
   UserAccount,
   DemandAnalysisDisplayDTO,
   DataClassification,
   DemandAnalysisQuestionStatus,
} from '../api/schemas/schema'
import { IDataContext } from '../interfaces/IDataContext'
import { IAnalysisStatusData } from '../interfaces/IAnalysisStatusData'
import { BadgeAnalysisQuestion } from '../components/demandAnalysis/BadgeAnalysisQuestion'
import { api } from './apiHelper'
import { handleMessage } from './stateHelper'
import React from 'react'
import { IComplianceState } from '../interfaces/IComplianceState'
import { Avatar } from '../components/common/avatar/Avatar'
import { RTFOutput } from '../components/common/outputs/RTFOutput'
import { t } from '../i18n/i18n'
import { getFullName } from './accountHelper'
import { MoreOptionsMenuButton } from '../components/common/other/MoreOptionsMenuButton'
import { IItemState } from '../interfaces/IItemState'
import { compareByDate, formatDate } from './stringHelper'
import { isSystemAdmin } from './permissionHelper'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Text } from '@fluentui/react-components'
dayjs.extend(utc)

export enum ComplianceTabs {
   Ongoing = 'ongoing',
   Concluded = 'concluded',
}

export enum DemandAssessmentTabs {
   Info = 'info',
   DemandAnalysis = 'demand-analysis',
   ActionPlan = 'actionplan',
}

export const getDemandAssessmentsColumns = (
   setComplianceState: React.Dispatch<React.SetStateAction<IComplianceState>>,
   userAccount: UserAccount
): TableColumnDefinition<DemandAnalysisDisplayDTO>[] => [
   {
      columnId: 'name',
      renderHeaderCell: () => {
         return t('AssessmentName')
      },
      renderCell: (item: DemandAnalysisDisplayDTO) => {
         return item.name
      },
      compare: (a, b) => {
         return a.name.localeCompare(b.name)
      },
   },
   {
      columnId: 'modifiedBy',
      renderHeaderCell: () => {
         return t('ModifiedBy')
      },
      renderCell: (item: DemandAnalysisDisplayDTO) => {
         return <Avatar name={getFullName(item.modifiedBy)} />
      },
      compare: (a, b) => {
         return getFullName(a.modifiedBy).localeCompare(getFullName(b.modifiedBy))
      },
   },
   {
      columnId: 'modified',
      renderHeaderCell: () => {
         return t('Modified')
      },
      renderCell: (item: DemandAnalysisDisplayDTO) => {
         return formatDate(item.modified, true)
      },
      compare: (a, b) => {
         return compareByDate(a.modified, b.modified)
      },
   },
   {
      columnId: 'menu',
      renderHeaderCell: () => <div style={{ width: '35px' }}>&nbsp;</div>,
      renderCell: (item) => (
         <MoreOptionsMenuButton
            item={item}
            disabled={!isDemandAssessmentOwnerOrCreator(item, userAccount) && !isSystemAdmin(userAccount)}
            disabledToolTipMessage={t('DeleteDemandAssessmentNoAccessMessage')}
            deleteFunction={deleteDemandAssessment}
            setState={setComplianceState}
            deleteMessage={t('DeleteDemandAssessment')}
            deleteConfirmationMessage={t('DeleteDemandAssessmentConfirmationMessage')}
         />
      ),
      compare: () => 0,
   },
]

export const getDefaultValues = (dataContext: IDataContext, complianceState: IComplianceState): DemandAnalysis => ({
   organisationId: dataContext.state.organisation.id,
   leadershipPerspectives: [],
   workplaceId: null,
   processes: [],
   subjects: [],
   analysisOwnerId: dataContext.state.userAccount.id,
   analysisOwner: dataContext.state.userAccount,
   dataClassification: DataClassification.InternalInformation,
   order: complianceState.allDemandAssessments.length + 1,
   analysisStart: dayjs().utc().toISOString(),
   analysisEnd: null,
   name: null,
   demandLevel: null,
})

export const getDemandAssessmentStatusColumns = (analysisStatusData: IAnalysisStatusData[]): TableColumnDefinition<IAnalysisStatusData>[] => {
   const columns: TableColumnDefinition<IAnalysisStatusData>[] = [
      {
         columnId: 'process',
         renderHeaderCell: () => {
            return t('Process')
         },
         renderCell: (item) => {
            return item.process
         },
         compare: (a, b) => {
            return a.process.localeCompare(b.process)
         },
      },
   ]

   if (analysisStatusData && analysisStatusData.some((item) => !!item.processOwner)) {
      columns.push({
         columnId: 'processOwner',
         renderHeaderCell: () => {
            return t('ProcessOwner')
         },
         renderCell: (item) => {
            return <Persona textAlignment="center" name={item.processOwner} />
         },
         compare: (a, b) => {
            return (a.processOwner ?? '').localeCompare(b.processOwner ?? '')
         },
      })
   }

   columns.push(
      {
         columnId: 'answered',
         renderHeaderCell: () => {
            return t('AllAnswered')
         },
         renderCell: (item) => {
            return `${item.numberOfAnsweredQuestions} (${item.numberOfTotalQuestions})`
         },
         compare: (a, b) => {
            return 1
         },
      },
      {
         columnId: 'status',
         renderHeaderCell: () => {
            return t('Status')
         },
         renderCell: (item) => {
            return item.status
         },
         compare: (a, b) => {
            return a.status.localeCompare(b.status)
         },
      }
   )

   return columns
}
const getDemandAnalysisProcessOwners = (demandAnalysisProcessOwners: DemandAnalysisProcessOwner[], processes: string[]) => {
   return processes.map((p) => {
      const owner = demandAnalysisProcessOwners.find((po) => po.process.text === p)
      if (!owner) return ''
      return owner.userAccount ? getFullName(owner.userAccount) : owner.ownerFreeText
   })
}

export const getCurrentAnalysisColumns = (
   demandAnalysisProcessOwners: DemandAnalysisProcessOwner[],
   showHeadings: boolean
): TableColumnDefinition<DemandAnalysisQuestion>[] => {
   const columns: TableColumnDefinition<DemandAnalysisQuestion>[] = [
      {
         columnId: 'number',
         renderHeaderCell: () => t('No.'),
         renderCell: (item) => item.number,
         compare: (a, b) => a.number - b.number,
      },
      {
         columnId: 'question',
         renderHeaderCell: () => t('Question'),
         renderCell: (item) => (
            <div>
               {showHeadings && item.demandUrlText ? (
                  <Text weight={item.demandLevel === DemandLevel.Two ? 'bold' : 'regular'} block>
                     {item.demandUrlText}
                  </Text>
               ) : null}
               <RTFOutput text={item.text} fontSize={300} noMargin />
            </div>
         ),
         compare: (a, b) => a.text.localeCompare(b.text),
      },
      {
         columnId: 'process',
         renderHeaderCell: () => t('Process'),
         renderCell: (item) => (
            <Text>
               {item.processes[0].split('/').map((part, i, arr) => (
                  <React.Fragment key={i}>
                     {part}
                     {i !== arr.length - 1 ? '/' : null}
                     {<wbr />}
                  </React.Fragment>
               ))}
            </Text>
         ),
         compare: (a, b) => a.processes[0].localeCompare(b.processes[0]),
      },
   ]

   if (demandAnalysisProcessOwners && demandAnalysisProcessOwners.length > 0) {
      columns.push({
         columnId: 'processOwner',
         renderHeaderCell: () => t('ProcessOwner'),
         renderCell: (item) => getDemandAnalysisProcessOwners(demandAnalysisProcessOwners, item.processes),
         compare: (a, b) => {
            const processOwnerA = demandAnalysisProcessOwners.find((po) => po.process.text === a.processes[0])
            const processOwnerB = demandAnalysisProcessOwners.find((po) => po.process.text === b.processes[0])
            return (processOwnerA?.userAccount?.firstName ?? '').localeCompare(processOwnerB?.userAccount?.firstName ?? '')
         },
      })
   }

   columns.push({
      columnId: 'status',
      renderHeaderCell: () => t('Status'),
      renderCell: (item) => <BadgeAnalysisQuestion status={item.status} />,
      compare: (a, b) => a.status - b.status,
   })

   return columns
}

export const filterQuestions = (selectedProcess: string, questions: DemandAnalysisQuestion[]): DemandAnalysisQuestion[] => {
   if (!questions) return []
   const level2Answers = new Map<string, string>()

   questions
      .filter((q) => q.demandLevel === DemandLevel.Two)
      .forEach((q) => {
         if (q.answerOptions?.some((opt) => opt.answerType === AnswerType.YesNo || opt.answerType === AnswerType.YesNoDontKnow) && q.id) {
            level2Answers.set(q.demandId, q.answer?.text || '')
         }
      })

   const filteredQuestions = questions.filter((q) => {
      if (q.demandLevel === DemandLevel.Three && q.demandParentId) {
         const parentAnswer = level2Answers.get(q.demandParentId)

         if (parentAnswer === undefined) {
            return true
         }

         if (parentAnswer !== 'Ja' && parentAnswer !== 'Vet inte') {
            return false
         }
      }
      return true
   })

   let filteredQuestionssByProcess = []
   if (selectedProcess === t('AllProcesses')) filteredQuestionssByProcess = filteredQuestions
   else filteredQuestionssByProcess = filteredQuestions.filter((x) => x.processes.some((p) => p === selectedProcess))

   return filteredQuestionssByProcess
}

export const getCompletedProcesses = (questions: DemandAnalysisQuestion[], processes: string[]): string[] => {
   if (!questions) return []

   return processes.filter((process) => {
      // Nivå 3-frågor visas bara beroende på svaret på sin nivå 2-fråga. Dolda frågor förblir obesvarade
      // och ska inte räknas med, så vi utgår från samma synlighetslogik som listan renderar.
      const visibleQuestions = filterQuestions(process, questions)

      return visibleQuestions.length > 0 && visibleQuestions.every((q) => q.status !== DemandAnalysisQuestionStatus.NotAnswered)
   })
}

export const getDemandAnalysisTitle = (selectedTab: string): string => {
   switch (selectedTab) {
      case DemandAssessmentTabs.Info:
         return t('Information')
      case DemandAssessmentTabs.ActionPlan:
         return t('ActionPlan')
      case DemandAssessmentTabs.DemandAnalysis:
         return null
      // return t('DemandAnalysis')
   }
}

export const deleteDemandAssessment = async (
   id: string,
   dataContext: IDataContext,
   setComplianceState: React.Dispatch<React.SetStateAction<IComplianceState>>
) => {
   try {
      const { organisation } = dataContext.state

      await api().deleteDemandAnalysisById(id)

      const response = await api().getDemandAnalysisByOrganisationId(organisation.id)
      const demandAssessments = response.data

      setComplianceState((prev) => ({ ...prev, allDemandAssessments: demandAssessments, open: false }))
      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyDeleted', { Item: t('DemandAssessment') }))
   } catch (error: any) {
      setComplianceState((prev) => ({ ...prev, open: false }))
      handleMessage(dataContext.setRootState, 'error', t('CouldNotDeleteItem', { Item: t('DemandAssessment') }))
   }
}

export const getProcessOwnersByWorkplaceId = async (
   workplaceId: string,
   dataContext: IDataContext,
   setProcessOwnerLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
   try {
      setProcessOwnerLoading(true)

      const response = await api().getProcessOwnersByWorkplaceId(workplaceId)
      return response.data
   } catch (error: any) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('ProcessOwner') }))
   } finally {
      setProcessOwnerLoading(false)
   }
}

export const getComplianceRelatedProperties = async (dataContext: IDataContext, setComplianceState: React.Dispatch<React.SetStateAction<IComplianceState>>) => {
   try {
      const [leadershipPerspectives, processes, subjects, demandComplianceModules] = await Promise.all([
         api().getAllLeadershipPerspectives(),
         api().getAllProcesss(),
         api().getAllSubjects(),
         api().getAllDemandModules(),
      ])
      setComplianceState((prev) => ({
         ...prev,
         leadershipPerspectives: leadershipPerspectives.data,
         processes: processes.data,
         subjects: subjects.data,
         demandComplianceModules: demandComplianceModules.data,
      }))
   } catch (error: any) {
      handleMessage(dataContext.setRootState, 'error', t('FetchFailed'), error.message)
   }
}

export const getClausesByDemandModuleId = async (
   demandModuleId: string,
   dataContext: IDataContext,
   setClausesLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
   try {
      setClausesLoading(true)

      const response = await api().getClausesByDemandModuleId(demandModuleId)
      return response.data
   } catch (error: any) {
      handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('ProcessOwner') }))
   } finally {
      setClausesLoading(false)
   }
}

export const isDemandAssessmentOwnerOrCreator = (demandAnalysis: DemandAnalysis, userAccount: UserAccount) => {
   if (demandAnalysis?.createdById === userAccount.id) return true
   if (demandAnalysis?.analysisOwnerId === userAccount.id) return true
   return false
}
export const isDemandAssessmentOwner = (demandAnalysis: DemandAnalysis, userAccount: UserAccount) => {
   if (demandAnalysis?.analysisOwnerId === userAccount.id) return true
   return false
}

export const isProcessOwnerForDemandAnalysisQuestion = (
   analysisState: IItemState<DemandAnalysis>,
   selectedItem: DemandAnalysisQuestion,
   userAccount: UserAccount
) => {
   if (analysisState.item && selectedItem) {
      const demandAnalysis = analysisState.item
      const userProcessOwnerProcesses = demandAnalysis?.demandAnalysisProcessOwners
         .filter((po) => po.userAccount?.id === userAccount.id)
         .map((po) => po.process.text)

      return selectedItem.processes.some((process) => userProcessOwnerProcesses.includes(process))
   }

   return false
}

export const isProcessOwnerOwnerOrSystemAdmin = (analysisState: IItemState<DemandAnalysis>, selectedItem: DemandAnalysisQuestion, userAccount: UserAccount) => {
   return (
      isProcessOwnerForDemandAnalysisQuestion(analysisState, selectedItem, userAccount) ||
      isDemandAssessmentOwner(analysisState.item, userAccount) ||
      isSystemAdmin(userAccount)
   )
}
